import { localDayKey } from "./days";
import { numberText } from "./progressChart";
import {
  snappedTestedMax, TESTED_MAX_BEATEN, TESTED_MAX_EXPIRED, type TestedMaxEntry, type TestedMaxEstimate, type TestedMaxStanding,
} from "./testedMax";
import { shortDate } from "./weekLine";

/* W125 part 2 (b) — THE TESTED MAX ON THE COACH WEB APP, the words and the reads around the generated rule (`testedMax.ts`,
 * emitted from `TestedMax.swift`; this file restates none of it). The phone's `TestedMaxBar` is the shape: one line under
 * the chart (`Tested 100 kg · Wed 12 Aug`, ` · beaten`, ` · over 8 weeks`), one button, an editor of one number on a 0.5 kg
 * ruler and the day it was lifted. The date is the web's grammar (`shortDate`, Proposal 7 rule 7), not the phone's `12 Aug`. */

/** The line the sheet shows; `null` = nothing typed. `signed` = the body is the load (the figure is the ADDED load). */
export function testedMaxLine(t: TestedMaxEntry | null, standing: TestedMaxStanding, signed: boolean): string {
  if (t === null) return "Tested max";
  const base = `Tested ${signed ? "+" : ""}${numberText(t.kg)} kg · ${shortDate(t.testedOn)}`;
  return standing === "beaten" ? base + TESTED_MAX_BEATEN : standing === "expired" ? base + TESTED_MAX_EXPIRED : base;
}

/** The chart's 1RM points as the rule's days — the coach's calendar, as every other day on this page (`localDayKey`). */
export function estimateDays(points: readonly { date: number; value: number }[]): TestedMaxEstimate[] {
  return points.map((p) => ({ date: localDayKey(new Date(p.date)), value: p.value }));
}

/** The latest `tested_maxes` row as an entry; anything unreadable is none (an absent value is not a value). */
export function entryFromRow(row: { max_kg: number | string | null; tested_on: string | null } | null | undefined): TestedMaxEntry | null {
  if (row === null || row === undefined || row.max_kg === null || row.tested_on === null) return null;
  const kg = typeof row.max_kg === "number" ? row.max_kg : Number(row.max_kg);
  const day = row.tested_on.slice(0, 10);
  return Number.isFinite(kg) && /^\d{4}-\d{2}-\d{2}$/.test(day) ? { kg, testedOn: day } : null;
}

/** The editor's first figure — the phone's: the typed max, else 100 kg (20 added when the body is the load), on the ruler. */
export function startKg(t: TestedMaxEntry | null, bodyIsLoad: boolean): number {
  return snappedTestedMax(t?.kg ?? (bodyIsLoad ? 20 : 100), bodyIsLoad);
}

/** A typed figure (`102,5` or `102.5`, a leading `+` allowed) on the ruler; `null` = not a number. */
export function typedKg(text: string, bodyIsLoad: boolean): number | null {
  const s = text.trim().replace(/^\+/, "").replace(",", ".");
  if (s === "" || !/^-?\d*\.?\d*$/.test(s)) return null;
  const v = Number(s);
  return Number.isFinite(v) ? snappedTestedMax(v, bodyIsLoad) : null;
}

/** `Lifted on` takes a real day, never after today (the phone's `in: ...Date.now`). */
export function liftedOnValid(day: string, today: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && !Number.isNaN(Date.parse(`${day}T00:00:00Z`)) && day <= today;
}

/** The INSERT — the grant's four columns; `entered_by` is the server's `auth.uid()`, never named here. */
export function insertRow(clientId: string, exerciseId: string, e: TestedMaxEntry) {
  return { client_id: clientId, coach_exercise_id: exerciseId, max_kg: e.kg, tested_on: e.testedOn };
}
